import { useState, useEffect, useContext } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { Link } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Sidebar from './Sidebar.js';
import { Form, Row, Col, Card, Table, Button, Image} from 'react-bootstrap';
// import DonationFilter from './DonationFilter';
import Pagination from 'react-bootstrap/Pagination';
import Breadcrumb from './Breadcrumb';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import icNoDonations from './assets/img/ic-empty-box.svg';
import DonateModal from './DonateModal';
import { isAdminType} from './utils/roleUtils';


const DonationsList = () => {
    const [donations, setDonations] = useState([]);
    const [filteredDonations, setFilteredDonations] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    
    const [donationsPerPage, setDonationsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastDonation = currentPage * donationsPerPage;
    const indexOfFirstDonation = indexOfLastDonation - donationsPerPage;
    const currentDonations = filteredDonations.slice(indexOfFirstDonation, indexOfLastDonation);
    const [searchQuery, setSearchQuery] = useState('');
    const { user } = useContext(UserContext);
    

    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderDate, setSortOrderDate] = useState('asc');
    const [sortOrderAmount, setSortOrderAmount] = useState('asc');
    
    const [tickets, setTickets] = useState([]);

    const [showDonateModal, setShowDonateModal] = useState(false);


    const [donateAmount, setDonateAmount] = useState(100);
    const [donateLoading, setDonateLoading] = useState(false);
    const [donateError, setDonateError] = useState('');

    const getAnyId = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        return String(val?._id || val?.id || '');
    };

    //  true if payment belongs to current logged-in user
    const isMyDonation = (payment) => {
        const myId = getAnyId(user);

        // ✅ support multiple backend shapes
        const ownerId =
            getAnyId(payment?.user) ||
            getAnyId(payment?.userId) ||
            getAnyId(payment?.createdBy) ||
            getAnyId(payment?.donation?.user) ||
            getAnyId(payment?.donation?.userId);

        return !!myId && !!ownerId && myId === ownerId;
    };


    const formatMoneyFromCents = (amountCents, currency = '€') => {
        if (!Number.isFinite(amountCents)) return '—';

        const major = Math.round(amountCents / 100);

        return new Intl.NumberFormat('en-US', {
            maximumFractionDigits: 0,
        }).format(major) + ` ${currency.toUpperCase()}`;
    };

    const getDonationDateMs = (d) => {
        // tries common shapes: donation.createdAt, createdAt, donation.date, date
        const raw =
            d?.donation?.createdAt ??
            d?.createdAt ??
            d?.donation?.date ??
            d?.date ??
            null;

        const ms = raw ? new Date(raw).getTime() : 0;
        return Number.isFinite(ms) ? ms : 0;
    };

    const formatDonationDate = (d) => {
        const ms = getDonationDateMs(d);
        if (!ms) return '—';
        return new Date(ms).toLocaleDateString(); // ✅ you can switch to toLocaleString() if you want time too
    };

    const getDonationAmountNum = (d) => {
        // tries common shapes: donation.amount, amount (string or number)
        const raw = d?.donation?.amount ?? d?.amount ?? '';
        const n = Number(String(raw).replace(',', '.'));
        return Number.isFinite(n) ? n : 0;
    };

    const formatDonationAmount = (d) => {
        const cents = d?.donation?.amount ?? d?.amount;
        const currency = d?.donation?.currency ?? '€';

        return formatMoneyFromCents(cents, currency);
    };


    // Sorting utilities
    const toggleOrder = (curr) => (curr === 'asc' ? 'desc' : 'asc');

    const sortDonationsByDate = () => {
        const next = toggleOrder(sortOrderDate) || 'asc';
        setSortOrderDate(next);
        setFilteredDonations((prev) => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const aMs = getDonationDateMs(a);
                const bMs = getDonationDateMs(b);
                return next === 'asc' ? aMs - bMs : bMs - aMs;
            });
            return arr;
        });
    };

    const sortDonationsByAmount = () => {
        const next = toggleOrder(sortOrderAmount) || 'asc';
        setSortOrderAmount(next);
        setFilteredDonations((prev) => {
            const arr = [...prev];
            arr.sort((a, b) => {
                const aAmt = getDonationAmountNum(a);
                const bAmt = getDonationAmountNum(b);
                return next === 'asc' ? aAmt - bAmt : bAmt - aAmt;
            });
            return arr;
        });
    };

    const sortDonationsByName = () => {
        const next = toggleOrder(sortOrderName) || 'asc';
        setSortOrderName(next);
        setFilteredDonations(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
            const aName = String(a?.donation?.name ?? '').toLowerCase();
            const bName = String(b?.donation?.name ?? '').toLowerCase();
            return next === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
            });
            return arr;
        });
    };

    useEffect(() => {
        if (!user) return;
        if (user?.role === 'donatorUser') return; // ✅ don't fetch tickets for donors

        const fetchTickets = async () => {
            try {
                const res = await api.get(`/api/tickets`);
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }
        };

        fetchTickets();
    }, [user]);

    useEffect(() => {
        if (!user) return;
        fetchDonations();
    }, [user]);

    const fetchDonations = async () => {
        try {
            const url = isAdminType(user?.role)
                ? '/api/payments/donations'          // ✅ admin full list
                : '/api/payments/donations/public';  // ✅ donatorUser safe list

            const res = await api.get(url);
            setDonations(res.data);
            setFilteredDonations(res.data);
        } catch (error) {
            console.error('Error fetching donations:', error);
        }
    };

    // Handle search filter
    useEffect(() => {
        const q = searchQuery.toLowerCase();

        const filteredResults = donations.filter((d) => {
            const name = String(d?.donation?.name ?? '').toLowerCase();
            return name.includes(q);
        });

        setFilteredDonations(filteredResults);
        setCurrentPage(1);
    }, [searchQuery, donations]);
    

    const totalPages = Math.ceil(filteredDonations.length / donationsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const noDonations = filteredDonations.length === 0;

    // ✅ Download donation certificate PDF (Blob -> file download)
    const downloadCertificate = async (donationItem) => {
        const donationId = donationItem?._id;
        if (!donationId) return;

        try {
            setDownloadingId(donationId); // ✅
            const res = await api.get(`/api/payments/donations/${donationId}/certificate`, {
                responseType: 'blob', // ✅ IMPORTANT
            });

            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `donation-certificate-${donationId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Certificate download failed:', err);
        } finally {
            setDownloadingId(null); // ✅
        }
    };

    const handleDonatePay = async (isPublic) => {
            setDonateError('');
    
            if (!Number.isInteger(donateAmount) || donateAmount <= 0) {
                setDonateError('Please enter a whole amount (1, 2, 5, …)');
                return;
            }
    
            setDonateLoading(true);
    
            try {
                const res = await api.post('/api/payments/create-checkout-session', {
                    amount: donateAmount,
                    isPublic: !!isPublic,
                });
    
                if (res.data?.url) {
                    window.location.href = res.data.url;
                } else {
                    setDonateError('No checkout URL returned from server.');
                }
            } catch (err) {
                console.error('Payment error:', err);
                setDonateError(err.response?.data?.message || err.message || 'Failed to start payment.');
            } finally {
                setDonateLoading(false);
            }
        };

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col className="mb-4">
                            <Card className="card-premium">
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col>
                                            <Card className="card-premium mb-1">
                                                <Card.Body>
                                                    <h1 className="mb-4">Donations</h1>
                                                    <Row >
                                                        <Col className="col-12 col-lg-6">
                                                            <h2 className="font-size-md mb-2">Why Donate?</h2>
                                                            <p className="font-size-md mb-4">
                                                                Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                                                            </p>
                                                            <Button variant="primary" onClick={() => setShowDonateModal(true)}>Make a Donation</Button>
                                                        </Col>
                                                        <Col className="col-12 col-lg-6">
                                                            <h2 className="font-size-md mb-2">About the certificate</h2>
                                                            <p className="font-size-md">
                                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                                                            </p>
                                                            <Link to="" className="font-size-md"><strong>See the certificate</strong></Link>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg={6}>
                                            <h2 className="py-5 mb-1">List of previous donations</h2>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12}>
                                            {/* <DonationFilter
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                donations={donations}
                                            /> */}
                                        </Col>
                                    </Row>
                                    {noDonations ? (
                                        <Row>
                                            <Col lg={12}>
                                                <Row className="mb-3">
                                                    <Col mlg={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                        <Image src={icNoDonations} alt="No Donations" className="mb-4" />
                                                        <h2 className="mb-3">No Donations yet.</h2>
                                                        <p className="mb-4">Your Donations will be displayed here.</p>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th className="th-fixed-city-id" onClick={() => sortDonationsByDate()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Date
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderDate === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderDate === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th className="th-fixed-city-name" onClick={() => sortDonationsByName()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Name
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderName === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderName === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th className="th-fixed-city-status" onClick={() => sortDonationsByAmount()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Amount
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderAmount === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderAmount === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th></th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {currentDonations.map((donation) => {
                                                                const nameVal = donation?.donation?.name ?? '';
                                                                return (
                                                                    <tr key={donation._id} style={{ cursor: 'pointer' }}>
                                                                        <td>{formatDonationDate(donation)}</td>
                                                                        <td>{nameVal || '—'}</td>
                                                                        <td>{formatDonationAmount(donation)}</td>
                                                                        <td className="text-end">
                                                                            {isMyDonation(donation) ? (
                                                                                <Button
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    onClick={() => downloadCertificate(donation)}
                                                                                    disabled={downloadingId === donation._id} // ✅
                                                                                >
                                                                                    {downloadingId === donation._id ? 'Downloading…' : 'View Certificate'}
                                                                                </Button>
                                                                            ) : (
                                                                                <span className="text-muted"></span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                
                                    {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center mb-4 mb-lg-0">
                                            <span>Total&nbsp;{filteredDonations.length} Donations</span>
                                            <Pagination>
                                                <Pagination.Prev 
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                                    disabled={currentPage === 1}
                                                />
                                                {[...Array(totalPages).keys()].map(number => (
                                                    <Pagination.Item 
                                                        key={number + 1} 
                                                        active={number + 1 === currentPage} 
                                                        onClick={() => handlePageChange(number + 1)}
                                                    >
                                                        {number + 1}
                                                    </Pagination.Item>
                                                ))}
                                                <Pagination.Next 
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={donationsPerPage}
                                                    onChange={(e) => setDonationsPerPage(Number(e.target.value))} // ✅ Updates state
                                                    style={{ width: 'auto', marginRight: '10px' }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>
                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>
                                            <Form.Control
                                                type="number"
                                                value={currentPage}
                                                className="pagination-controls-goto-input"
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage); // ✅ Only valid pages
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                }}
                                                style={{ textAlign: 'center' }}
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            {/* Donate Modal */}
            <DonateModal
                show={showDonateModal}
                onHide={() => setShowDonateModal(false)}
                currency="€"
                amount={donateAmount}
                setAmount={setDonateAmount}
                loading={donateLoading}
                error={donateError}
                onPay={handleDonatePay}
            />
        </>
        
    );
};

export default DonationsList;
